import React, { useEffect, useRef, Children, cloneElement, isValidElement } from 'react';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import { content } from './fullscreenOverlay.styles';
import { FullscreenOverlayHeader } from './FullscreenOverlayHeader';
import { FullscreenOverlayBody } from './FullscreenOverlayBody';
import { addToOverlayStack, removeFromOverlayStack, isTopmostOverlay } from './FullscreenOverlayPortal';
import type { FullscreenOverlayContentProps } from './types';

/**
 * FullscreenOverlayContent Component
 *
 * Main content container for the fullscreen overlay.
 * Handles focus management, keyboard navigation, and variant-specific styling.
 *
 * Features:
 * - Focus trap: Tab/Shift+Tab cycle through focusable elements within content
 * - ESC key closes the overlay
 * - Auto-focus: First focusable element receives focus when opened
 * - Focus return: Focus returns to trigger element when closed
 * - Variant styling: Different layouts for media, detail, and standard variants
 *
 * @example
 * <FullscreenOverlay.Content variant="standard">
 *   <FullscreenOverlay.Header>...</FullscreenOverlay.Header>
 *   <FullscreenOverlay.Body>...</FullscreenOverlay.Body>
 *   <FullscreenOverlay.Footer>...</FullscreenOverlay.Footer>
 * </FullscreenOverlay.Content>
 */
export const FullscreenOverlayContent = ({
  children,
  variant = 'standard',
  autoFocus = true,
  className,
  ariaLabelledBy,
}: FullscreenOverlayContentProps) => {
  const { close, isOpen } = useFullscreenOverlay();
  const contentRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);
  const scrollContainerRef = useRef<HTMLElement>(null);
  const overlayIdRef = useRef<string>(Math.random().toString(36).substr(2, 9));

  // Focus management
  useEffect(() => {
    if (autoFocus) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement;

      // Focus the content container or first focusable element from main content
      const initialFocusElements = getInitialFocusElements();
      if (initialFocusElements.length > 0) {
        initialFocusElements[0].focus();
      } else if (contentRef.current) {
        contentRef.current.focus();
      }

      // Return focus to previous element when unmounted
      return () => {
        if (previousActiveElement.current && 'focus' in previousActiveElement.current) {
          (previousActiveElement.current as HTMLElement).focus();
        }
      };
    }
  }, [autoFocus]);

  // Stack-aware ESC key handler - only closes topmost overlay in hierarchy
  useEffect(() => {
    if (!isOpen) return;

    const overlayId = overlayIdRef.current;

    // Add to overlay stack when opened
    addToOverlayStack(overlayId);

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Only handle ESC if this is the topmost overlay
        if (isTopmostOverlay(overlayId)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          close();
        }
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
      removeFromOverlayStack(overlayId);
    };
  }, [isOpen, close]);

  /**
   * Get focusable elements for initial focus - prioritizes close button, then main content
   */
  const getInitialFocusElements = (): HTMLElement[] => {
    if (!contentRef.current) return [];

    const focusableSelector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
    ].join(', ');

    // PRIORITY 1: Look for close button in header first
    const headerElement = contentRef.current.querySelector('header');
    if (headerElement) {
      const closeButtons = Array.from(
        headerElement.querySelectorAll('button[aria-label*="Close"], button[aria-label*="close"]'),
      ) as HTMLElement[];
      if (closeButtons.length > 0) {
        return closeButtons;
      }

      // If no explicit close button, try first button in header
      const headerButtons = Array.from(headerElement.querySelectorAll('button:not([disabled])')) as HTMLElement[];
      if (headerButtons.length > 0) {
        return headerButtons;
      }
    }

    // PRIORITY 2: Try to find focusable elements in main content
    const mainContent = contentRef.current.querySelector('main');
    if (mainContent) {
      const mainFocusableElements = Array.from(mainContent.querySelectorAll(focusableSelector)) as HTMLElement[];
      if (mainFocusableElements.length > 0) {
        return mainFocusableElements;
      }
    }

    // PRIORITY 3: Fall back to all focusable elements if no header or main content
    return Array.from(contentRef.current.querySelectorAll(focusableSelector)) as HTMLElement[];
  };

  /**
   * Get all focusable elements within the content for focus trapping
   */
  const getFocusableElements = (): HTMLElement[] => {
    if (!contentRef.current) return [];

    const focusableSelector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
    ].join(', ');

    return Array.from(contentRef.current.querySelectorAll(focusableSelector)) as HTMLElement[];
  };

  /**
   * Handle keyboard navigation
   * Note: ESC key is handled at document level for consistent behavior across focus states
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'Tab': {
        // Implement focus trap
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;

        if (event.shiftKey) {
          // Shift + Tab: if on first element, go to last
          if (activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: if on last element, go to first
          if (activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
        break;
      }
    }
  };

  const combinedClassName = [content({ variant }), className].filter(Boolean).join(' ');

  // Clone children to pass scroll container ref to Header and Body components
  const enhancedChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) {
      return child;
    }

    // Pass scrollContainerRef to Header component for scroll fade functionality
    if (child.type === FullscreenOverlayHeader) {
      return cloneElement(child, {
        ...child.props,
        scrollContainerRef,
      });
    }

    // Pass ref to Body component for scroll container access
    if (child.type === FullscreenOverlayBody) {
      return cloneElement(child, {
        ...child.props,
        ref: scrollContainerRef,
      });
    }

    return child;
  });

  return (
    <div
      ref={contentRef}
      className={combinedClassName}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {enhancedChildren}
    </div>
  );
};
